import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, Loader2, CheckCircle2, XCircle, CreditCard } from "lucide-react";
import { base44, voultCheckout } from "@voult/api/base44Client";

const field = "w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm outline-none focus:border-slate-900 focus:ring-2 focus:ring-slate-900/5";

const Result = ({ res, onReset }) => {
  const ok = res.status === "approved";
  return (
    <div data-testid="voult-checkout-result" className={`rounded-2xl border p-6 ${ok ? "border-emerald-200 bg-emerald-50" : "border-rose-200 bg-rose-50"}`}>
      <div className="flex items-center gap-2 mb-3">
        {ok ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <XCircle className="h-5 w-5 text-rose-600" />}
        <p data-testid="voult-checkout-status" className="font-semibold capitalize">{res.status}</p>
      </div>
      <p className="text-sm text-slate-700">{res.currency} {res.amount.toFixed(2)} · {res.merchant} · {res.masked_number}</p>
      {res.decline_reason && <p data-testid="voult-checkout-decline" className="text-sm text-rose-600 mt-1">{res.decline_reason}</p>}
      {res.tx_hash && <p className="text-xs text-slate-500 mt-2 break-all">TXHS <code data-testid="voult-checkout-txhash" className="font-mono">{res.tx_hash}</code></p>}
      <p className="text-xs text-slate-500 mt-1">Ref {res.reference}</p>
      <div className="flex gap-3 mt-4">
        <button data-testid="voult-checkout-again" onClick={onReset} className="rounded-xl bg-slate-900 px-4 py-2 text-sm text-white hover:bg-slate-800">New charge</button>
        <Link to="/voult/transactions" className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">View transactions</Link>
      </div>
    </div>
  );
};

export default function Checkout() {
  const [cards, setCards] = useState(null);
  const [form, setForm] = useState({ card_id: "", amount: "", merchant: "", description: "", cvv: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [res, setRes] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: k === "cvv" ? e.target.value.replace(/\D/g, "").slice(0, 4) : e.target.value }));

  useEffect(() => {
    base44.entities.Card.list("-created_date").then((c) => {
      setCards(c);
      const pre = new URLSearchParams(window.location.search).get("card");
      setForm((f) => ({ ...f, card_id: pre || c[0]?.id || "" }));
    });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const out = await voultCheckout({ ...form, amount: parseFloat(form.amount), currency: "USD" });
      setRes(out);
      base44.entities.ActivityLog.create({ action: `payment_${out.status}`, entity_type: "PaymentTransaction", entity_id: out.id, details: `${out.merchant} USD ${out.amount.toFixed(2)} (${out.masked_number})` }).catch(() => {});
    } catch (err) {
      setError(err.response?.data?.detail?.toString() || "Charge failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="p-6 max-w-lg mx-auto" data-testid="voult-checkout-page">
      <h1 className="text-2xl font-semibold text-slate-900 mb-1 flex items-center gap-2"><ShoppingCart className="h-6 w-6" />Checkout</h1>
      <p className="text-sm text-slate-500 mb-6">Charge a saved card. Payments are processed by CITIDAN Processor and recorded in the ledger.</p>
      {res ? <Result res={res} onReset={() => { setRes(null); setForm((f) => ({ ...f, amount: "", description: "", cvv: "" })); }} /> :
        cards === null ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> :
        cards.length === 0 ? (
          <div className="text-center py-16 text-slate-500"><CreditCard className="h-10 w-10 mx-auto mb-3 text-slate-300" />
            <p>No saved cards.</p><Link to="/voult/add-card" className="text-sm text-indigo-600 hover:underline">Add a card first →</Link></div>
        ) : (
          <form onSubmit={submit} className="space-y-4 bg-white rounded-2xl border border-slate-200 p-6">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">Card</label>
              <select data-testid="voult-checkout-card" value={form.card_id} onChange={set("card_id")} className={field}>
                {cards.map((c) => <option key={c.id} value={c.id}>{(c.nickname || c.cardholder_name)} · {c.brand} •••• {c.last4} · {c.expiry_month}/{c.expiry_year}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Amount (USD)</label>
                <input data-testid="voult-checkout-amount" type="number" min="0.01" step="0.01" required value={form.amount} onChange={set("amount")} className={`${field} font-mono`} />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">CVC</label>
                <input data-testid="voult-checkout-cvv" type="password" inputMode="numeric" required minLength={3} value={form.cvv} onChange={set("cvv")} className={`${field} font-mono`} />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">Merchant</label>
              <input data-testid="voult-checkout-merchant" required value={form.merchant} onChange={set("merchant")} placeholder="Acme Store" className={field} />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">Description (optional)</label>
              <input data-testid="voult-checkout-description" value={form.description} onChange={set("description")} className={field} />
            </div>
            {error && <p data-testid="voult-checkout-error" className="text-sm text-rose-500">{error}</p>}
            <button data-testid="voult-checkout-submit" type="submit" disabled={busy}
              className="w-full flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60">
              {busy ? <><Loader2 className="h-4 w-4 animate-spin" />Processing…</> : "Charge card"}
            </button>
            <p className="text-[11px] text-slate-400 text-center">CVC is verified for this charge only and never stored.</p>
          </form>
        )}
    </div>
  );
}
